import { isEmpty, isNil, omit } from 'lodash'

import ApiHandler from '@/api/base'
import { OKR_LEVEL_ENTITY_KEYS } from '@/utils/entity-keys'
import { isKR } from '@/utils/objective-types'
import { ELEMENT_TYPES_CATEGORIES, PROGRESS_OPTIONS_DEFAULT_VALUE } from '@/utils/okr-levels'

const normalizeLevelModel = level => {
  if (isKR({ [OKR_LEVEL_ENTITY_KEYS.TYPE_ID]: level[OKR_LEVEL_ENTITY_KEYS.TYPE_ID] })) {
    return level
  }

  return omit(level, [OKR_LEVEL_ENTITY_KEYS.FIELD_TYPE_IDS])
}

class LevelApiHandler extends ApiHandler {
  /**
   *
   * @param {null | Array<string | number>} levelIds
   * @param {null | Array<string | number>} workspaceIds
   * @param {null | string} searchString
   * @returns {Promise<any>}
   */
  async getLevels({ levelIds = null, workspaceIds = null, searchString = null } = {}) {
    const { data } = await this.http.post('/getLevels', {
      levelIds,
      workspaceIds,
      searchString
    })

    return data
  }

  async getLevelById({ levelId = null } = {}) {
    const [level] = await this.getLevels({ levelIds: [levelId] })
    return level
  }

  async createLevel({
    color = null,
    name,
    prefix = null,
    orderValue = -1,
    workspaceIds,
    addToAllWorkspaces = false,
    allowWithoutParent = false,
    fieldTypeIds = PROGRESS_OPTIONS_DEFAULT_VALUE,
    typeId = ELEMENT_TYPES_CATEGORIES.OBJECTIVE.typeId
  } = {}) {
    const { data } = await this.http.post(
      '/createLevel',
      normalizeLevelModel({
        color,
        name,
        prefix,
        workspaceIds,
        orderValue,
        addToAllWorkspaces,
        allowWithoutParent,
        fieldTypeIds,
        typeId
      })
    )
    return data
  }

  async updateLevel({
    color = null,
    levelId = null,
    name,
    prefix = null,
    workspaceIds,
    orderValue,
    fieldTypeIds = PROGRESS_OPTIONS_DEFAULT_VALUE,
    allowWithoutParent = false,
    typeId
  } = {}) {
    const payload = normalizeLevelModel({
      color,
      levelId,
      name,
      prefix,
      workspaceIds,
      orderValue,
      fieldTypeIds,
      allowWithoutParent,
      typeId
    })

    const { data } = await this.http.post(
      '/updateLevel',
      omit(payload, [OKR_LEVEL_ENTITY_KEYS.TYPE_ID])
    )
    return data
  }

  async removeLevel({ levelId } = {}) {
    const { data } = await this.http.post('/removeLevel', {
      levelId
    })
    return data
  }

  async checkDeleteLevel({ levelId } = {}) {
    const { data } = await this.http.get('/checkDeleteLevel', {
      params: {
        levelId
      }
    })
    return data
  }

  async reassignLevels({ oldLevelId, newLevels } = {}) {
    const { data } = await this.http.post('/levels/reassign', {
      oldLevelId,
      newLevels
    })
    return data
  }

  async getLevelsForFilter({ workspaceId = null, workspaceIds } = {}) {
    const isWorkspaceIdsPassed = !isNil(workspaceIds) && Array.isArray(workspaceIds)

    if (isWorkspaceIdsPassed && isEmpty(workspaceIds)) {
      throw new Error('workspaceIds must be a non-empty array')
    }

    if (!isWorkspaceIdsPassed && isNil(workspaceId)) {
      throw new Error('workspaceId or workspaceIds is required')
    }

    const payload = isWorkspaceIdsPassed ? { workspaceIds } : { workspaceId }

    const { data } = await this.http.post('/getLevelsForFilter', payload)

    return data
  }

  async bulkUnlinkWorkspacesFromLevel({ levelId = null, workspaceIds = [] } = {}) {
    const { data } = await this.http.post('/bulkRemoveLevelFromWorkspaces', {
      levelId,
      workspaceIds
    })

    return data
  }

  async unlinkWorkspaceFromLevel({ levelId = null, workspaceId = null } = {}) {
    return await this.bulkUnlinkWorkspacesFromLevel({
      levelId,
      workspaceIds: [workspaceId]
    })
  }

  async linkWorkspaceToLevel({
    levelId = null,
    workspaceIds = [],
    addToAllWorkspaces = false
  } = {}) {
    const { data } = await this.http.post('/bulkAddLevelToWorkspaces', {
      id: levelId,
      workspaceIds,
      addToAllWorkspaces
    })

    return data
  }
}

export default LevelApiHandler
